import {
  Box,
  Button,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import browser from "@raiden/library/constants/browser";
import { POSTS_TYPE_VALUE_ASSOCIATION } from "@raiden/library/constants/posts";
import useLocale from "@raiden/library/hooks/useLocale";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Icon } from "../../../../components/Icon";

/**
 * @param {object} params
 * @param {string} params.tag
 * @returns {object}
 */
function getHeadingStyle({ tag }) {
  switch (tag) {
    case "h1":
      return {
        marginLeft: "0",
        fontSize: "1.125rem",
      };
    case "h2":
      return {
        marginLeft: "0",
        fontSize: "1rem",
      };
    case "h3":
      return {
        marginLeft: "1rem",
        fontSize: "0.875rem",
      };
    case "h4":
      return {
        marginLeft: "1.5rem",
        fontSize: "0.75rem",
      };
    case "h5":
      return {
        marginLeft: "2rem",
        fontSize: "0.75rem",
      };
    case "h6":
      return {
        marginLeft: "2.5rem",
        fontSize: "0.75rem",
      };
    default:
      return {
        marginLeft: "0",
        fontSize: "1rem",
      };
  }
}

/**
 * @typedef {object} Heading
 * @property {string} tag
 * @property {string} text
 */

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Post").Post} post
 */
/**
 * @param {Props} props
 */
export function PostSummary({ post }) {
  const { current_revision } = post || {};

  const { locale, defaultLocale } = useLocale();

  const offset = useBreakpointValue({ base: 80, lg: 160 }) ?? 100;

  const isAssociation = post?.type === POSTS_TYPE_VALUE_ASSOCIATION;
  const canBeContacted = Boolean(
    current_revision?.asso_email ||
      (current_revision?.asso_phone_country &&
        current_revision?.asso_phone_number) ||
      (current_revision?.asso_zipcode && current_revision?.asso_city),
  );

  const headings = useMemo(() => {
    /** @type {Heading[]} */
    const headings = [];

    if (!browser) {
      return headings;
    }

    post?.current_revision?.content?.lines.forEach((line) => {
      line.columns.forEach((blocks) => {
        blocks.forEach((block) => {
          if (block.type === "text") {
            if (locale && defaultLocale) {
              const localizedBlockData =
                block.content.intl[locale] ?? block.content.intl[defaultLocale];
              if (localizedBlockData) {
                const html = localizedBlockData.text;
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");

                // Get headings h1, h2
                for (let i = 1; i <= 2; i++) {
                  const tag = `h${i}`;
                  const elements = doc.querySelectorAll(tag);
                  Array.from(elements).forEach((element) => {
                    if (!element.textContent) {
                      return;
                    }
                    /** @type {Heading} */
                    const heading = {
                      tag,
                      text: element.textContent,
                    };
                    headings.push(heading);
                  });
                }
              }
            }
          }
        });
      });
    });

    return headings;
  }, [defaultLocale, locale, post?.current_revision?.content?.lines]);

  /**
   * @param {object} params
   * @param {Heading} params.heading
   */
  function focusHeading({ heading }) {
    const elements = document.querySelectorAll(heading.tag);
    for (let element of elements) {
      if (
        element.textContent !== null &&
        element.textContent.trim() === heading.text.trim()
      ) {
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;

        const offsetPosition = elementPosition - offset; // Pixel to go under the header

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
        break;
      }
    }
  }

  if (headings.length <= 1 && !canBeContacted) {
    return null;
  }

  return (
    <Box backgroundColor="brandPrimary.100" pl={{ base: "0", md: "105px" }}>
      <Stack spacing="12px" p="16px">
        <Text variant="h3" fontWeight={500} color="brandPrimary.700">
          {isAssociation ? (
            <FormattedMessage defaultMessage="Contact" />
          ) : (
            <FormattedMessage defaultMessage="Sommaire" />
          )}
        </Text>

        {canBeContacted && (
          <Stack>
            {current_revision?.asso_zipcode && current_revision?.asso_city && (
              <HStack>
                <Icon icon="ms_place" color="gray.400" size="1.5rem" />

                <Text fontSize="1rem" color="gray.600">
                  <FormattedMessage
                    defaultMessage={"{address} {zipcode} {city}"}
                    values={{
                      address: current_revision?.asso_address2
                        ? `${current_revision?.asso_address1}, ${current_revision?.asso_address2}`
                        : current_revision?.asso_address1,
                      zipcode: current_revision?.asso_zipcode,
                      city: current_revision?.asso_city,
                    }}
                  />
                </Text>
              </HStack>
            )}

            {current_revision?.asso_phone_country &&
              current_revision?.asso_phone_number && (
                <HStack>
                  <Icon icon="ms_phone" color="gray.400" size="1.5rem" />

                  <Text fontSize="1rem" color="gray.600">
                    <Link
                      href={`tel:${current_revision.asso_phone_number}`}
                      display="inline-block">
                      {current_revision.asso_phone_number
                        .replace("+33", "0")
                        .replace(/(..)/g, "$1 ")}
                    </Link>
                  </Text>
                </HStack>
              )}

            {current_revision?.asso_email && (
              <HStack>
                <Icon icon="ms_mail" color="gray.400" size="1.5rem" />

                <Text fontSize="1rem" color="gray.600" wordBreak="break-word">
                  <Link
                    href={`mailto:${current_revision.asso_email}`}
                    target="_blank">
                    {current_revision.asso_email}
                  </Link>
                </Text>
              </HStack>
            )}
          </Stack>
        )}

        {headings.length > 0 && (
          <>
            <Stack display={{ base: "none", md: "flex" }}>
              {headings.map((heading, index) => (
                <Box key={index}>
                  <Button
                    maxW="full"
                    onClick={() => {
                      focusHeading({ heading });
                    }}
                    variant="link"
                    textAlign="left">
                    <Text
                      marginLeft={
                        getHeadingStyle({ tag: heading.tag }).marginLeft
                      }
                      as="span"
                      fontSize={getHeadingStyle({ tag: heading.tag }).fontSize}
                      variant="menu-dropdown"
                      color="gray.600"
                      whiteSpace="pre-wrap">
                      {heading.text}
                    </Text>
                  </Button>
                </Box>
              ))}
            </Stack>

            <Box display={{ base: "block", md: "none" }}>
              <Menu matchWidth={true}>
                <MenuButton
                  as={Button}
                  rightIcon={<Icon icon="ms_keyboard_arrow_down" />}
                  textAlign="left"
                  w="full">
                  <FormattedMessage defaultMessage="Parcourir" />
                </MenuButton>

                <MenuList>
                  {headings.map((heading, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        focusHeading({ heading });
                      }}>
                      <Text as="span" variant="menu-dropdown" color="gray.600">
                        {heading.text}
                      </Text>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}
